import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Layout from './MortgageAffordability/components/Layout';
import NumField from './MortgageAffordability/components/NumField';
import SegmentToggle from './MortgageAffordability/components/SegmentToggle';
import {
  computeMortgageAffordability,
  fmtUSD,
  DTI_TARGET,
} from './MortgageAffordability/lib/math';

const INITIAL = {
  address: '',
  homePrice: 0,
  downPayment: 20,
  downPaymentType: 'percent', // 'dollar' | 'percent'
  interestRate: 7,
  termYears: 30,
  loanType: 'Conventional',
  propertyTaxes: 1.2,
  taxType: 'percent', // 'dollar' | 'percent'
  insurance: 0,
  pmiRate: 0.6,
  hoaMonthly: 0,
  cddAmount: 0,
  cddSchedule: 'Monthly',
  grossMonthlyIncome: 0,
  otherMonthlyDebt: 0,
};

const SCOPED_CSS = `
  .maa-root { font-family: 'Poppins', -apple-system, BlinkMacSystemFont, sans-serif; color: #092825; }
  .maa-root .maa-num::-webkit-outer-spin-button,
  .maa-root .maa-num::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
  .maa-root .maa-num { -moz-appearance: textfield; }
`;

const TERM_OPTIONS = [10, 15, 20, 25, 30];
const LOAN_TYPE_OPTIONS = ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo'];
const CDD_SCHEDULE_OPTIONS = [
  { value: 'Monthly',   label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Annually',  label: 'Annually' },
];
const DOLLAR_PCT_OPTIONS = [
  { value: 'dollar',  label: '$' },
  { value: 'percent', label: '%' },
];

export default function MortgageAffordabilityPage() {
  const [inputs, setInputs] = useState(INITIAL);
  const update = (patch) => setInputs((prev) => ({ ...prev, ...patch }));

  const result = useMemo(() => computeMortgageAffordability(inputs), [inputs]);
  const showAffordability = inputs.grossMonthlyIncome > 0 && inputs.homePrice > 0;
  const hasHomePrice = inputs.homePrice > 0;
  const overDti = showAffordability && result.qualified === false;

  return (
    <Layout>
      <Helmet>
        <title>Free Mortgage &amp; Affordability Calculator · I Need Numbers</title>
        <meta
          name="description"
          content="Free mortgage and affordability calculator for real estate agents and buyers. Estimate the monthly payment, PITI breakdown, DTI, and max home price — including HOA and CDD fees. No login, nothing saved."
        />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <style>{SCOPED_CSS}</style>

      <div className="maa-root" data-testid="maa-root">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-6">
          <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
            Free tool · I Need Numbers
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#092825] mb-2">
            Mortgage &amp; Affordability
          </h1>
          <p className="text-slate-700 max-w-2xl">
            See the monthly payment, PITI breakdown, and what a buyer can actually
            afford — including HOA and CDD. Fill in what you know; nothing is saved
            or sent.
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

            {/* Inputs — left 3 cols */}
            <div className="lg:col-span-3 space-y-6" data-testid="maa-inputs">

              {/* Card 1 — Property & loan */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#092825] mb-4">Property &amp; loan</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <label className="block text-sm sm:col-span-2">
                    <span className="text-[#092825] font-medium">Address (optional)</span>
                    <input
                      type="text"
                      value={inputs.address}
                      onChange={(e) => update({ address: e.target.value })}
                      placeholder="123 Main St"
                      data-testid="maa-input-address"
                      className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
                    />
                  </label>

                  <NumField
                    label="Home price"
                    prefix="$"
                    value={inputs.homePrice}
                    onChange={(v) => update({ homePrice: v })}
                    help="The agreed-upon (or target) purchase price."
                    testId="maa-input-home-price"
                  />

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#092825] font-medium">Down payment</span>
                      <SegmentToggle
                        value={inputs.downPaymentType}
                        onChange={(v) => update({ downPaymentType: v })}
                        options={DOLLAR_PCT_OPTIONS}
                        ariaLabel="Down payment type"
                        testId="maa-toggle-downpayment"
                      />
                    </div>
                    <NumField
                      label=""
                      prefix={inputs.downPaymentType === 'dollar' ? '$' : undefined}
                      suffix={inputs.downPaymentType === 'percent' ? '%' : undefined}
                      value={inputs.downPayment}
                      onChange={(v) => update({ downPayment: v })}
                      help="Toggle between a dollar amount and a percent of the home price."
                      testId="maa-input-down-payment"
                    />
                  </div>

                  <NumField
                    label="Interest rate"
                    suffix="%"
                    value={inputs.interestRate}
                    onChange={(v) => update({ interestRate: v })}
                    help="Annual mortgage interest rate. Get a current quote from your lender for the most accurate result."
                    testId="maa-input-interest-rate"
                  />

                  <label className="block text-sm">
                    <span className="text-[#092825] font-medium">Loan term (years)</span>
                    <select
                      value={inputs.termYears}
                      onChange={(e) => update({ termYears: Number(e.target.value) })}
                      data-testid="maa-input-term-years"
                      className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
                    >
                      {TERM_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>

                  <label className="block text-sm">
                    <span className="text-[#092825] font-medium">Loan type</span>
                    <select
                      value={inputs.loanType}
                      onChange={(e) => update({ loanType: e.target.value })}
                      data-testid="maa-input-loan-type"
                      className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
                    >
                      {LOAN_TYPE_OPTIONS.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Card 2 — Recurring housing costs */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#092825] mb-4">Recurring housing costs</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-[#092825] font-medium">Property taxes</span>
                      <SegmentToggle
                        value={inputs.taxType}
                        onChange={(v) => update({ taxType: v })}
                        options={DOLLAR_PCT_OPTIONS}
                        ariaLabel="Property tax input mode"
                        testId="maa-toggle-tax"
                      />
                    </div>
                    <NumField
                      label=""
                      prefix={inputs.taxType === 'dollar' ? '$' : undefined}
                      suffix={inputs.taxType === 'percent' ? '%' : undefined}
                      value={inputs.propertyTaxes}
                      onChange={(v) => update({ propertyTaxes: v })}
                      help="Toggle between an annual dollar amount and an annual percent of home value (millage)."
                      testId="maa-input-property-taxes"
                    />
                  </div>

                  <NumField
                    label="Homeowners insurance (annual)"
                    prefix="$"
                    value={inputs.insurance}
                    onChange={(v) => update({ insurance: v })}
                    help="Annual homeowners insurance premium. Florida coastal areas often run $3,000–$8,000."
                    testId="maa-input-insurance"
                  />

                  <NumField
                    label="PMI rate (annual %)"
                    suffix="%"
                    value={inputs.pmiRate}
                    onChange={(v) => update({ pmiRate: v })}
                    help="Annual private mortgage insurance rate, applied only when LTV > 80%. Typical: 0.3%–1.5%."
                    testId="maa-input-pmi-rate"
                  />

                  <NumField
                    label="HOA (monthly)"
                    prefix="$"
                    value={inputs.hoaMonthly}
                    onChange={(v) => update({ hoaMonthly: v })}
                    help="Monthly homeowners-association dues, if any."
                    testId="maa-input-hoa"
                  />

                  <NumField
                    label="CDD fees"
                    prefix="$"
                    value={inputs.cddAmount}
                    onChange={(v) => update({ cddAmount: v })}
                    help="Community Development District fees are common in Florida master-planned communities (Lakewood Ranch, The Villages, Wiregrass, etc.). Paid in addition to property taxes."
                    testId="maa-input-cdd-amount"
                  />

                  <label className="block text-sm">
                    <span className="text-[#092825] font-medium">CDD schedule</span>
                    <select
                      value={inputs.cddSchedule}
                      onChange={(e) => update({ cddSchedule: e.target.value })}
                      data-testid="maa-input-cdd-schedule"
                      className="mt-1 w-full rounded border border-slate-300 focus:border-[#2FA163] focus:ring-1 focus:ring-[#2FA163] outline-none px-3 py-2 text-sm bg-white"
                    >
                      {CDD_SCHEDULE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              {/* Card 3 — Affordability check */}
              <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
                <h2 className="text-lg font-bold text-[#092825] mb-1">Affordability check</h2>
                <p className="text-xs text-slate-500 mb-4">
                  Fill these in to see qualification status and max affordable price.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <NumField
                    label="Gross monthly income"
                    prefix="$"
                    value={inputs.grossMonthlyIncome}
                    onChange={(v) => update({ grossMonthlyIncome: v })}
                    help="Pre-tax monthly income from all sources."
                    testId="maa-input-gross-income"
                  />
                  <NumField
                    label="Other monthly debt"
                    prefix="$"
                    value={inputs.otherMonthlyDebt}
                    onChange={(v) => update({ otherMonthlyDebt: v })}
                    help="Recurring debt payments — auto loans, student loans, credit-card minimums. Excludes rent/mortgage."
                    testId="maa-input-other-debt"
                  />
                </div>
              </div>
            </div>

            {/* Sticky summary — right 2 cols */}
            <aside className="lg:col-span-2">
              <div className="lg:sticky lg:top-24 flex flex-col gap-5">
                <div
                  className="bg-white rounded-lg border border-slate-200 shadow-sm p-6"
                  data-testid="maa-summary"
                >
                  {/* Section A — Loan summary */}
                  <h2 className="text-lg font-bold text-[#092825] mb-4">Loan summary</h2>
                  <div className="space-y-1.5 text-sm">
                    <div className="flex justify-between py-1 border-b border-slate-100" data-testid="maa-line-home-price">
                      <span className="text-slate-600">Home price</span>
                      <span className="tabular-nums font-semibold">{fmtUSD(result.loanAmount + result.downPaymentAmount)}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-100" data-testid="maa-line-down-payment">
                      <span className="text-slate-600">Down payment</span>
                      <span className="tabular-nums font-semibold">
                        {fmtUSD(result.downPaymentAmount)}
                        <span className="ml-2 text-xs text-slate-400 font-normal">
                          ({hasHomePrice ? `${(result.downPaymentAmount / inputs.homePrice * 100).toFixed(1)}%` : '—'})
                        </span>
                      </span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-100" data-testid="maa-line-loan-amount">
                      <span className="text-slate-600">Loan amount</span>
                      <span className="tabular-nums font-semibold">{fmtUSD(result.loanAmount)}</span>
                    </div>
                    <div className="flex justify-between py-1" data-testid="maa-line-ltv">
                      <span className="text-slate-600">LTV</span>
                      <span className="tabular-nums font-semibold">
                        {hasHomePrice ? `${result.ltv.toFixed(1)}%` : '—'}
                      </span>
                    </div>
                  </div>

                  {/* Section B — Monthly payment breakdown */}
                  <h2 className="text-lg font-bold text-[#092825] mt-6 mb-3">Monthly payment</h2>
                  {result.breakdown.length === 0 && (
                    <p className="text-sm text-slate-500 italic py-2" data-testid="maa-empty-hint">
                      Enter a home price to see your monthly payment.
                    </p>
                  )}
                  {result.breakdown.map((it) => (
                    <div
                      key={it.key}
                      className="flex justify-between text-sm py-1.5 border-b border-slate-100"
                      data-testid={`maa-line-${it.key}`}
                    >
                      <span className="text-slate-600">{it.label}</span>
                      <span className="tabular-nums">{fmtUSD(it.amount, { cents: true })}</span>
                    </div>
                  ))}

                  <div className="mt-4 pt-4 border-t-2 border-slate-300">
                    <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">
                      Total monthly payment
                    </div>
                    <div
                      className="text-3xl sm:text-4xl font-bold tabular-nums text-[#2FA163]"
                      data-testid="maa-total-piti"
                    >
                      {hasHomePrice ? fmtUSD(result.piti, { cents: true }) : '—'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">PITI + HOA + CDD</div>
                  </div>

                  {/* Section C — Affordability check */}
                  {showAffordability && (
                    <div className="mt-6 pt-4 border-t border-slate-200" data-testid="maa-affordability">
                      <h2 className="text-lg font-bold text-[#092825] mb-3">Affordability check</h2>

                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between py-1 border-b border-slate-100" data-testid="maa-line-max-allowed-piti">
                          <span className="text-slate-600">Max allowed PITI (at {DTI_TARGET}% DTI)</span>
                          <span className="tabular-nums font-semibold">
                            {result.maxAllowedPITI != null && result.maxAllowedPITI > 0
                              ? fmtUSD(result.maxAllowedPITI, { cents: true })
                              : '—'}
                          </span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100" data-testid="maa-line-dti">
                          <span className="text-slate-600">DTI</span>
                          <span className="tabular-nums font-semibold">
                            {result.dti != null ? `${result.dti.toFixed(1)}%` : '—'}
                          </span>
                        </div>
                        <div className="flex justify-between py-1" data-testid="maa-line-max-affordable">
                          <span className="text-slate-600">Max affordable home price</span>
                          <span className="tabular-nums font-semibold">
                            {result.maxAffordablePrice != null && result.maxAffordablePrice > 0
                              ? fmtUSD(result.maxAffordablePrice)
                              : '—'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4">
                        {overDti ? (
                          <div
                            className="rounded-md bg-amber-50 border border-amber-200 text-amber-800 px-3 py-2 text-sm font-semibold"
                            data-testid="maa-badge-over-dti"
                          >
                            Over {DTI_TARGET}% DTI — explore lower price or longer term
                          </div>
                        ) : (
                          <div
                            className="rounded-md border px-3 py-2 text-sm font-semibold"
                            style={{ background: 'rgba(22,163,74,0.08)', borderColor: 'rgba(22,163,74,0.3)', color: '#0d7a36' }}
                            data-testid="maa-badge-qualified"
                          >
                            Qualified at {DTI_TARGET}% DTI
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Always-on lender disclaimer — renders regardless of input state. */}
                  <p
                    className="mt-6 pt-4 border-t border-slate-200 text-xs font-bold text-red-600 leading-relaxed"
                    data-testid="maa-lender-disclaimer"
                  >
                    Estimate only — not a substitute for a lender's pre-qualification.
                    Always consult a licensed lender before making mortgage or
                    home-purchase decisions.
                  </p>
                </div>

                {/* Disclosures */}
                <div className="bg-slate-100 border border-slate-200 rounded p-4 text-xs text-slate-600 leading-relaxed space-y-2">
                  <p>Estimate only. Final figures depend on lender pricing, escrow, taxes, and insurance quotes.</p>
                  <p>PMI auto-applies when the loan-to-value ratio is above 80%. Some loan types (VA, USDA) do not use PMI in the same way — confirm with the lender.</p>
                  <p>CDD fees are common in Florida master-planned communities; they are billed in addition to property taxes. Use the schedule that matches the bill (monthly via escrow, quarterly, or once-a-year on the tax bill).</p>
                </div>

                {/* Upgrade CTA */}
                <div
                  className="bg-white rounded-lg border border-[#2FA163]/30 shadow-md p-8"
                  data-testid="maa-upgrade-cta"
                >
                  <div className="text-xs uppercase tracking-wider text-[#2FA163] font-semibold mb-2">
                    Want to hand this to your buyer?
                  </div>
                  <h3 className="text-lg font-bold text-[#092825] mb-3">
                    Turn this into a branded pre-qualification PDF with a 7-day free trial.
                  </h3>
                  <ul className="text-sm text-slate-700 space-y-1.5 mb-5">
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      Branded PDFs with your logo, colors, and contact info
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      Saved buyer history with shareable links
                    </li>
                    <li className="flex gap-2">
                      <span className="text-[#2FA163] font-bold">✓</span>
                      AI coaching on what to negotiate next
                    </li>
                  </ul>
                  <Link
                    to="/pricing?source=mortgage-affordability&plan=pro"
                    data-testid="maa-trial-cta-btn"
                    className="inline-block w-full text-center text-white font-semibold px-6 py-3 rounded shadow transition-opacity hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #2FA163 0%, #286C4E 100%)' }}
                  >
                    Start my 7-day trial →
                  </Link>
                </div>
              </div>
            </aside>

          </div>
        </section>
      </div>
    </Layout>
  );
}
